import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  Box,
  TextFieldSize,
  Text,
  IconSize,
  SideSheet,
  SideSheetHeader,
  SideSheetSize,
  SideSheetBody,
  SideSheetClose,
  SideSheetFooter,
  Separator,
  SeparatorType,
  theme,
  Checkbox,
  ButtonSize,
  ButtonAppearance,
  Spinner,
  SpinnerSize,
  ToastAction,
  useToastsDispatch,
  ToastType,
} from '@bechtel/gds';
import { t, Trans } from '@lingui/macro';
import { get, map, findIndex, groupBy, reduce, filter, toLower, includes } from 'lodash';
import { useName, DrawerFooterButtonStyled } from '@bechtel/client-common';
import {
  BulkRodRequestProgressInput,
  BulkRodRequestProgressWeldsPayload,
  FilterOperator,
  PagingInput,
  ProgressRodRequestWeldErrors,
  RodRequest,
  RodRequestFilterByTerm,
  RodRequestFilterInput,
  RodRequestList,
  RodRequestSortByTerm,
  RodRequestWeld,
  RodRequestWeldStatus,
  SortOrder,
} from '@bechtel/types';
import { PROGRESS_ROD_REQUESTS, BULK_ROD_REQUEST_PROGRESS } from '@bechtel/client-gql';
import { useMutation, useLazyQuery } from '@apollo/client';
import { Formik } from 'formik';
import { ErrorRetry, ErrorBoundary } from '@bechtel/client-common';

import { AvatarRegularStyled, CircleStyled, CheckboxContainer, SearchFieldComponentStyled, WeldRow } from './styled';
import { AssignedWeldSidesheetProps, CheckboxType, FormikState, RodRequestProgressWeld } from './types';
import { ProgressRodRequestError, ProgressRodRequestErrorType } from './error';

const getInititalFormikState = (rodRequestList: RodRequest[]) => {
  const welds = {};
  const didNotWeld = {};
  if (rodRequestList && rodRequestList.length > 0) {
    rodRequestList?.forEach((rodRequest) => {
      didNotWeld[rodRequest.id] = false;
      rodRequest.welds.forEach((weld) => {
        const progressWeldIndex = findIndex(
          get(rodRequest, ['stencils', 0, 'welds'], []),
          (item: RodRequestWeld) => item.weld.id === weld.id
        );
        if (progressWeldIndex !== -1) {
          if (rodRequest['stencils'][0]['welds'][progressWeldIndex].status === RodRequestWeldStatus.INPROGRESS) {
            welds[rodRequest.id + weld.id] = {
              [CheckboxType.COMPLETED]: false,
              [CheckboxType.WELDED]: true,
              weldId: weld.id,
              rodRequestId: rodRequest.id,
            };
          } else if (rodRequest['stencils'][0]['welds'][progressWeldIndex].status === RodRequestWeldStatus.COMPLETED) {
            welds[rodRequest.id + weld.id] = {
              [CheckboxType.COMPLETED]: true,
              [CheckboxType.WELDED]: true,
              weldId: weld.id,
              rodRequestId: rodRequest.id,
            };
          } else {
            welds[rodRequest.id + weld.id] = {
              [CheckboxType.COMPLETED]: false,
              [CheckboxType.WELDED]: false,
              weldId: weld.id,
              rodRequestId: rodRequest.id,
            };
            didNotWeld[rodRequest.id] = true;
          }
        } else {
          welds[rodRequest.id + weld.id] = {
            [CheckboxType.COMPLETED]: false,
            [CheckboxType.WELDED]: false,
            weldId: weld.id,
            rodRequestId: rodRequest.id,
          };
        }
      });
    });
  }
  return { welds, didNotWeld };
};

export const ProgressWeldsToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        {t`The selected welds have been succesfully progressed.`}
      </Text>
    </Box>
  );
};

export const AssignedWeldSidesheet: React.FC<AssignedWeldSidesheetProps> = ({
  welder,
  onClose,
  onSave,
  requestNumber,
  rodRequestProgressWeld,
}) => {
  const { getFullName } = useName();
  const [searchWeldIdText, setSearchWeldIdText] = useState('');
  const debounceSearchQuery = useDeferredValue(searchWeldIdText?.trim());
  const [weldsStatus, setWeldsStatus] = useState<FormikState>({ welds: {}, didNotWeld: {} });
  const [allCheckStatus, setAllCheckStatus] = useState({
    [CheckboxType.WELDED]: false,
    [CheckboxType.COMPLETED]: false,
  });
  const [bulkRodRequestProgressError, setBulkRodRequestProgressError] = useState<ProgressRodRequestErrorType | null>(
    null
  );
  const [errorRodRequests, setErrorRodRequests] = useState<ProgressRodRequestWeldErrors[] | null>(null);
  const [rodRequestWeldList, setRodRequestWeldList] = useState<RodRequestProgressWeld[] | []>([]);
  const dispatchToast = useToastsDispatch();
  const [getRodRequests, { data, loading, refetch, error }] = useLazyQuery<
    { rodRequests: RodRequestList },
    {
      input: RodRequestFilterInput;
      paging: PagingInput;
    }
  >(PROGRESS_ROD_REQUESTS, {
    notifyOnNetworkStatusChange: true,
  });

  const rodrequestItems = useMemo(() => {
    return get(data, 'rodRequests.items', []);
  }, [data]);
  let rodRequestList = rodRequestProgressWeld ? rodRequestProgressWeld : rodrequestItems;
  const [bulkRodRequestProgress, { loading: bulkRodRequestProgressLoading }] = useMutation<
    { progressRodRequestWelds: BulkRodRequestProgressWeldsPayload },
    { input: BulkRodRequestProgressInput }
  >(BULK_ROD_REQUEST_PROGRESS, {
    onCompleted: (data) => {
      const errorRodRequests = get(data, 'progressRodRequestWelds.rodRequestProgressPayload.error');
      if (errorRodRequests?.length) {
        setErrorRodRequests(errorRodRequests);
      } else {
        setBulkRodRequestProgressError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: () => <ProgressWeldsToast />,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
        if (onSave) {
          onSave(data.progressRodRequestWelds.rodRequestProgressPayload.rodRequest || []);
        }
        onClose();
      }
    },
    onError: (error) => {
      setBulkRodRequestProgressError(error);
    },
  });

  useEffect(() => {
    setWeldsStatus(getInititalFormikState(rodRequestList));
    setRodRequestWeldList(rodRequestList);
  }, [rodRequestList]);

  useEffect(() => {
    if (debounceSearchQuery?.length > 0 && debounceSearchQuery?.length < 3) return;
    const complexFilter = [
      {
        term: RodRequestFilterByTerm.ASSIGNEE_ID,
        value: welder?.id,
        operator: FilterOperator.EQUALS,
      },
    ];
    if (debounceSearchQuery.length) {
      complexFilter.push({
        term: RodRequestFilterByTerm.WELD_TAG,
        value: debounceSearchQuery,
        operator: FilterOperator.CONTAINS,
      });
    }
    if (requestNumber) {
      complexFilter.push({
        term: RodRequestFilterByTerm.ROD_REQUEST_NUMBER,
        value: requestNumber,
        operator: FilterOperator.EQUALS,
      });
    }
    if (debounceSearchQuery.length > 0 && rodRequestProgressWeld) {
      filterRodRequestsByWeldTag();
    } else if (rodRequestProgressWeld) {
      setWeldsStatus(getInititalFormikState(rodRequestList));
      setRodRequestWeldList(rodRequestList);
    }
    if (!rodRequestProgressWeld) {
      getRodRequests({
        variables: {
          input: {
            complexFilter: {
              and: complexFilter,
            },
            sortByFilter: {
              sortOrder: SortOrder.DESC,
              term: RodRequestSortByTerm.REQUEST_TIME,
            },
          },
          paging: { cursor: '1', limit: 50 },
        },
      });
    }
  }, [debounceSearchQuery, rodRequestProgressWeld]);

  const filterRodRequestsByWeldTag = () => {
    rodRequestList = filter(rodRequestList, (rodRequest) => {
      const searchValue = toLower(debounceSearchQuery);
      const tag = toLower(map(rodRequest.welds, 'tag').join(','));
      return includes(tag, searchValue);
    });
    setWeldsStatus(getInititalFormikState(rodRequestList));
    setRodRequestWeldList(rodRequestList);
  };

  const checkAll = (field: string, values, setFieldValue) => {
    if (field === CheckboxType.WELDED && allCheckStatus[CheckboxType.WELDED] === true) {
      const newStatus = { [CheckboxType.WELDED]: false, [CheckboxType.COMPLETED]: false };
      setAllCheckStatus(newStatus);
      for (const weld in values.welds) {
        setFieldValue(`welds.${weld}`, { ...values['welds'][weld], ...newStatus });
      }
    } else if (field === CheckboxType.COMPLETED && allCheckStatus[CheckboxType.COMPLETED] === false) {
      const newStatus = { [CheckboxType.WELDED]: true, [CheckboxType.COMPLETED]: true };
      for (const weld in values.welds) {
        setFieldValue(`welds.${weld}`, { ...values['welds'][weld], ...newStatus });
      }
      setAllCheckStatus({ ...allCheckStatus, [field]: !allCheckStatus[field] });
    } else {
      for (const weld in values.welds) {
        setFieldValue(`welds.${weld}.${field}`, !allCheckStatus[field]);
      }
      setAllCheckStatus({ ...allCheckStatus, [field]: !allCheckStatus[field] });
    }
  };

  const closeErrorModal = () => {
    setBulkRodRequestProgressError(null);
    setErrorRodRequests(null);
  };

  return (
    <SideSheet size={SideSheetSize.DEFAULT} visible>
      <SideSheetHeader
        title={t`Assigned Welds (${weldsStatus && weldsStatus?.welds ? Object.keys(weldsStatus?.welds)?.length : ''})`}
      >
        <SideSheetClose onClick={() => onClose()}></SideSheetClose>
      </SideSheetHeader>
      <Formik
        enableReinitialize={true}
        initialValues={weldsStatus}
        onSubmit={(values) => {
          const rodRequests: any = groupBy(Object.values(values['welds']), 'rodRequestId');
          const payload = {
            welderId: welder.id,
            progressRodRequestWeldsInput: map(Object.keys(rodRequests), (rodRequestId) => {
              const progressRodRequestWeldInput = values['didNotWeld'][rodRequestId]
                ? []
                : reduce(
                    rodRequests[rodRequestId],
                    (acc: { weldId: string; status: RodRequestWeldStatus }[], weld) => {
                      const status = weld[CheckboxType.COMPLETED]
                        ? RodRequestWeldStatus.COMPLETED
                        : weld[CheckboxType.WELDED]
                        ? RodRequestWeldStatus.INPROGRESS
                        : undefined;
                      if (status) {
                        acc.push({ weldId: weld.weldId, status });
                      }
                      return acc;
                    },
                    []
                  );
              return {
                rodRequestId: rodRequestId,
                didNotWeld: values['didNotWeld'][rodRequestId] || (progressRodRequestWeldInput.length ? false : true),
                progressRodRequestWeldInput,
              };
            }),
          };
          bulkRodRequestProgress({
            variables: {
              input: payload,
            },
          });
        }}
      >
        {({ isValid, dirty, setFieldValue, handleSubmit, values }) => {
          return (
            <>
              <SideSheetBody>
                <Box flexDirection={{ sm: 'column' }} style={{ width: '100%' }}>
                  <Box
                    py={{ sm: '450' }}
                    pr={{ sm: '550' }}
                    pl={{ sm: '450' }}
                    justifyContent={{ sm: 'space-between' }}
                  >
                    <Box flexDirection={{ sm: 'row' }} style={{ gap: theme.spacings['200'] }}>
                      <Box>
                        <AvatarRegularStyled data-testid="avatar" src={welder?.imageUrl} />
                      </Box>
                      <Box
                        flexDirection={{ sm: 'column' }}
                        justifyContent={{ sm: 'center' }}
                        style={{ gap: theme.spacings['100'] }}
                      >
                        <Text size="400" color="grey.600" weight="600">
                          {getFullName(welder?.firstName, welder?.lastName)}
                        </Text>
                        <Text size="200" color="grey.600">
                          {welder?.badgeNumber}
                        </Text>
                      </Box>
                    </Box>
                    <Box data-testid="search-weld-id">
                      <SearchFieldComponentStyled
                        size={TextFieldSize.SMALL}
                        placeholder={t`Search Weld Tag`}
                        iconSize={IconSize.MEDIUM}
                        type="search"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchWeldIdText(e.target.value)}
                        value={searchWeldIdText}
                      />
                    </Box>
                  </Box>
                  <Separator
                    size="100%"
                    type={SeparatorType.HORIZONTAL}
                    spacing="000"
                    style={{ backgroundColor: theme.colors.grey['300'] as string }}
                  />
                  {loading && (
                    <Box mt={{ sm: '300' }} justifyContent={{ sm: 'center' }} style={{ width: '100%' }}>
                      <Spinner size={SpinnerSize.LARGE} />
                    </Box>
                  )}
                  {!loading && error && (
                    <ErrorRetry refetch={refetch}>
                      <Trans>
                        There was a problem loading Rod Request List. Please click the button below to try again.
                      </Trans>
                    </ErrorRetry>
                  )}
                  {!loading && rodRequestWeldList?.length ? (
                    <>
                      <WeldRow disabled={Object.values(values['didNotWeld']).includes(true)}>
                        <CheckboxContainer onClick={() => checkAll(CheckboxType.WELDED, values, setFieldValue)}>
                          <Checkbox
                            data-testid="all-welded"
                            size={TextFieldSize.MEDIUM}
                            checked={allCheckStatus[CheckboxType.WELDED] || false}
                            readOnly
                          />
                          <Text size="300" color="grey.700">
                            <Trans>All Welded</Trans>
                          </Text>
                        </CheckboxContainer>
                        <CheckboxContainer
                          disabled={!allCheckStatus[CheckboxType.WELDED]}
                          onClick={() => checkAll(CheckboxType.COMPLETED, values, setFieldValue)}
                        >
                          <Checkbox
                            data-testid="all-completed"
                            size={TextFieldSize.MEDIUM}
                            checked={allCheckStatus[CheckboxType.COMPLETED || false]}
                            readOnly
                          />
                          <Text size="300" color="grey.700">
                            <Trans>All Completed</Trans>
                          </Text>
                        </CheckboxContainer>
                      </WeldRow>
                      {rodRequestWeldList &&
                        rodRequestWeldList?.length > 0 &&
                        map(rodRequestWeldList, (rodRequest: RodRequest) => {
                          return (
                            <Box key={rodRequest.id} flexDirection={{ sm: 'column' }}>
                              <Box
                                py={{ sm: '300' }}
                                pl={{ sm: '500' }}
                                css={{ backgroundColor: theme.colors.grey['200'] as string, width: '100%' }}
                              >
                                <Text data-testid={`rod-request-${rodRequest.id}`} size="300" color="grey.1000">
                                  {rodRequest?.requestNumber}
                                </Text>
                              </Box>
                              {map(rodRequest.welds, (weld) => {
                                const weldedStatus = get(
                                  values,
                                  ['welds', rodRequest.id + weld.id, CheckboxType.WELDED],
                                  false
                                );
                                const completedStatus = get(
                                  values,
                                  ['welds', rodRequest.id + weld.id, CheckboxType.COMPLETED],
                                  false
                                );

                                return (
                                  <WeldRow
                                    disabled={values['didNotWeld'][rodRequest.id]}
                                    key={rodRequest.id + weld.id}
                                    showBorder
                                  >
                                    <CheckboxContainer
                                      onClick={() => {
                                        setFieldValue(
                                          `welds.${rodRequest.id + weld.id}.${CheckboxType.WELDED}`,
                                          !weldedStatus
                                        );
                                        setFieldValue(
                                          `welds.${rodRequest.id + weld.id}.${CheckboxType.COMPLETED}`,
                                          false
                                        );
                                      }}
                                    >
                                      <Checkbox
                                        data-testid={`welded-checkbox-${rodRequest.id + weld.id}`}
                                        size={TextFieldSize.MEDIUM}
                                        checked={weldedStatus || false}
                                        readOnly
                                      />
                                      <Box flexDirection={{ sm: 'column' }} style={{ gap: theme.spacings['200'] }}>
                                        <Text size="400" weight="600" color="grey.1000">
                                          {weld.tag}
                                        </Text>
                                        <Box alignItems={{ sm: 'center' }}>
                                          {weld?.pipeMaterial && (
                                            <>
                                              <Text size="300" color="grey.700">
                                                {weld.pipeMaterial}
                                              </Text>
                                              <CircleStyled />
                                            </>
                                          )}
                                          {weld?.type?.name && (
                                            <>
                                              <Text size="300" color="grey.700">
                                                {weld.type.name}
                                              </Text>
                                              <CircleStyled />
                                            </>
                                          )}
                                          <Text size="300" color="grey.700">
                                            {`${weld.pipeDiameter}"`}&nbsp;
                                            <Trans>Diameter</Trans>
                                          </Text>
                                        </Box>
                                      </Box>
                                    </CheckboxContainer>
                                    <CheckboxContainer
                                      pr={{ sm: '700' }}
                                      disabled={!weldedStatus}
                                      onClick={() => {
                                        setFieldValue(
                                          `welds.${rodRequest.id + weld.id}.${CheckboxType.COMPLETED}`,
                                          !completedStatus
                                        );
                                      }}
                                    >
                                      <Checkbox
                                        data-testid={`completed-checkbox-${rodRequest.id + weld.id}`}
                                        size={TextFieldSize.MEDIUM}
                                        checked={completedStatus || false}
                                        readOnly
                                      />
                                      <Text size="300" color="grey.700">
                                        <Trans>Completed</Trans>
                                      </Text>
                                    </CheckboxContainer>
                                  </WeldRow>
                                );
                              })}
                              <Box
                                py={{ sm: '450' }}
                                pl={{ sm: '500' }}
                                alignItems={{ sm: 'center' }}
                                gap={{ sm: '500' }}
                                style={{
                                  cursor: 'pointer',
                                  borderBottom: `1px solid ${theme.colors.grey['300']}`,
                                }}
                                onClick={() => {
                                  setFieldValue(
                                    `didNotWeld.${rodRequest.id}`,
                                    !get(values, ['didNotWeld', rodRequest.id])
                                  );
                                }}
                              >
                                <Checkbox
                                  data-testid={`did-not-weld-checkbox-${rodRequest.id}`}
                                  size={TextFieldSize.MEDIUM}
                                  checked={get(values, ['didNotWeld', rodRequest.id]) || false}
                                  readOnly
                                />
                                <Text size="400" weight="600" color="grey.1000">
                                  <Trans>Did not weld</Trans>
                                </Text>
                              </Box>
                            </Box>
                          );
                        })}
                    </>
                  ) : null}
                </Box>
              </SideSheetBody>
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }} style={{ gap: theme.spacings['400'] }}>
                  <Box>
                    <DrawerFooterButtonStyled
                      size={ButtonSize.MEDIUM}
                      appearance={ButtonAppearance.SECONDARY}
                      onClick={() => onClose()}
                    >
                      <Trans>Cancel</Trans>
                    </DrawerFooterButtonStyled>
                  </Box>
                  <Box>
                    <DrawerFooterButtonStyled
                      size={ButtonSize.MEDIUM}
                      appearance={ButtonAppearance.SECONDARY}
                      disabled={!dirty || !isValid}
                      onClick={handleSubmit}
                    >
                      {bulkRodRequestProgressLoading ? <Spinner size={SpinnerSize.SMALL} /> : <Trans>Save</Trans>}
                    </DrawerFooterButtonStyled>
                  </Box>
                </Box>
              </SideSheetFooter>
            </>
          );
        }}
      </Formik>
      {(bulkRodRequestProgressError || errorRodRequests) && (
        <ErrorBoundary>
          <ProgressRodRequestError
            error={bulkRodRequestProgressError}
            errorRodRequests={errorRodRequests}
            onClose={closeErrorModal}
          />
        </ErrorBoundary>
      )}
    </SideSheet>
  );
};
